<template>
    <div class="widget-time-period w-full">
        <div class="flex mb-3">
            <RedesignCheckbox
                v-model="timeModel.is_calendrical"
                :true-label="1"
                :false-label="0"
            >
                <span class="main-text-color">{{ $t('widget.config.isCalendrical') }}</span>
            </RedesignCheckbox>
        </div>
        <div class="flex flex-col">
            <div class="period-select mb-3">
                <RedesignFormLabel
                    :label="$t('widget.config.periodType')"
                    class="mb-2"
                />
                <RedesignBaseSelect
                    v-model="timeModel.period_type"
                    placeholder="Select"
                    full-width
                    :multiple="false"
                    @change="onChangeSelect"
                >
                    <RedesignSelectOption
                        v-for="(item, index) of widgetTimeOptionsForPeriod"
                        :key="index"
                        :label="$t(item.label)"
                        :value="item.value"
                    />
                </RedesignBaseSelect>
            </div>
            <div class="flex flex-col">
                <RedesignFormLabel
                    :label="$t('widget.config.periodsBack')"
                    class="mb-3"
                />
                <RedesignInputNumber
                    :key="minValForCheckbox"
                    v-model="timeModel.periods_back"
                    :step="1"
                    :min-value="minValForCheckbox"
                    :max-value="maxValue"
                    placeholder="0"
                />
            </div>

            <div v-if="timeModel.period_type === 'week' && timeModel.is_calendrical" class="mt-3">
                <RedesignCheckbox
                    v-model="timeModel.is_monday_first"
                    :true-label="1"
                    :false-label="0"
                >
                    <span class="main-text-color">{{ $t('widget.config.fromMonday') }} </span>
                </RedesignCheckbox>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import RedesignSelectOption from '@/components/RedesignBaseComponents/Inputs/RedesignSelectOption.vue'
import { widgetTimeOptionsForPeriod } from '@/enum/widgetTimeOptions'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import { WidgetTime } from '@/types/dashboard'
import { useVModel } from '@vueuse/core'

const props = defineProps<{
    value: WidgetTime
}>()
const emit = defineEmits([ 'input', 'update:value' ])

const timeModel = useVModel(props, 'value', emit, { eventName: 'input' })
const minValForCheckbox = ref(0)
const maxValue = ref(10)

function onChangeSelect (value: string) {
    const widgetTimePeriod = widgetTimeOptionsForPeriod.find(el => el.value === value)
    timeModel.value.period_type = value
    maxValue.value = widgetTimePeriod?.max || 10
    timeModel.value.periods_back = Math.min((timeModel.value.periods_back || 0), maxValue.value)
    timeModel.value.label = widgetTimePeriod?.label
}

function initData () {
    const defaultPeriodType = timeModel.value.period_type ? timeModel.value.period_type : widgetTimeOptionsForPeriod[0].value
    if (timeModel.value.periods_back === undefined) {
        timeModel.value.periods_back = timeModel.value.is_calendrical ? 0 : 1
    }
    timeModel.value.period_type = defaultPeriodType
    maxValue.value = widgetTimeOptionsForPeriod.find(el => el.value === timeModel.value.period_type)?.max || 10
    minValForCheckbox.value = timeModel.value.is_calendrical ? 0 : 1
}

watch(
    () => timeModel.value.is_calendrical,
    (val) => {
        minValForCheckbox.value = val ? 0 : 1
    }
)

onMounted(initData)
</script>

